import Image from 'next/image';
import Link from 'next/link';

import { ArrowRight } from 'lucide-react';

const BANNER_BG = '#1c0b1a';

/**
 * "Create your own AI companion" banner above the footer. The photo fills the
 * whole card (its left side is an empty dark bedroom made for the copy); a
 * soft left shade keeps the text readable. Critical visuals are inline styles
 * (the dev build drops uncommon utility classes).
 */
export function CreateCompanionBanner() {
  return (
    <div
      className="relative overflow-hidden"
      style={{
        borderRadius: 22,
        background: BANNER_BG,
        border: '1px solid rgba(236,72,153,0.45)',
        boxShadow:
          '0 0 0 1px rgba(236,72,153,0.08), 0 18px 50px -20px rgba(236,72,153,0.55)',
      }}
    >
      {/* Phones: the three women as a strip on top. sm and up: the whole card. */}
      <div
        className="relative w-full sm:hidden"
        style={{ aspectRatio: '2 / 1' }}
      >
        <BannerPhoto fade="bottom" />
      </div>
      <div className="absolute inset-0 hidden sm:block">
        <BannerPhoto fade="left" />
      </div>

      <div className="relative z-10 flex flex-col gap-3 px-6 pt-2 pb-7 sm:max-w-[40%] sm:px-8 sm:py-6">
        <div>
          <h3 className="text-2xl leading-tight font-bold text-white sm:text-[24px] lg:text-[26px]">
            Create your own
            <span
              className="block"
              style={{
                background: 'linear-gradient(90deg, #ff9ed2, #ec4899)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              AI companion
            </span>
          </h3>
        </div>

        <p className="max-w-md text-sm leading-relaxed text-white/70">
          Design her looks, personality, and story. Make it uniquely yours in
          just a few clicks.
        </p>

        <Link
          href="/create"
          data-test="create-companion-banner-cta"
          className="mt-1 inline-flex w-fit items-center gap-3 text-sm font-semibold text-white transition-[filter,box-shadow] hover:brightness-110 sm:text-base"
          style={{
            padding: '11px 28px',
            borderRadius: 9999,
            background: 'linear-gradient(180deg, #f472b6 0%, #db2777 100%)',
            boxShadow: '0 10px 30px -10px rgba(236,72,153,0.85)',
          }}
        >
          Create Now
          <ArrowRight style={{ width: 18, height: 18 }} />
        </Link>
      </div>
    </div>
  );
}

function BannerPhoto({ fade }: { fade: 'left' | 'bottom' }) {
  return (
    <>
      <Image
        src="/images/banners/create-companion-v2.webp"
        alt="Three AI companions created on Edem AI"
        fill
        sizes="(max-width: 640px) 100vw, 1300px"
        className="object-cover"
        style={{ objectPosition: fade === 'left' ? '100% 55%' : '78% 30%' }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            fade === 'left'
              ? 'linear-gradient(90deg, rgba(20,6,18,0.85) 0%, rgba(20,6,18,0.55) 28%, rgba(20,6,18,0) 50%)'
              : `linear-gradient(0deg, ${BANNER_BG} 0%, rgba(28,11,26,0) 45%)`,
        }}
      />
    </>
  );
}
